import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Search,
  Package,
  Boxes,
  ArrowLeftRight,
  MessageSquare,
  CreditCard,
  Users,
  BarChart3,
  Building2,
  Percent,
  LineChart,
  Upload,
  SlidersHorizontal,
  Bug,
  Bell,
  Code2,
  ShieldCheck,
  BookOpen,
  HelpCircle,
  ChevronRight,
} from 'lucide-react';

import { GuideSectionVote } from '@/components/guide/GuideSectionVote';

// Maps category id -> icon + color. Text comes from i18n.
const CATEGORY_META = {
  gettingStarted: {
    icon: <Package className="h-5 w-5 text-orange-500" />,
    color: 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800',
  },
  marketplace: {
    icon: <Boxes className="h-5 w-5 text-blue-500" />,
    color: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
  },
  trading: {
    icon: <ArrowLeftRight className="h-5 w-5 text-emerald-500" />,
    color: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
  },
  messaging: {
    icon: <MessageSquare className="h-5 w-5 text-cyan-500" />,
    color: 'bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-800',
  },
  subscriptions: {
    icon: <CreditCard className="h-5 w-5 text-violet-500" />,
    color: 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800',
  },
  groups: {
    icon: <Users className="h-5 w-5 text-amber-500" />,
    color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
  },
  sellerPortal: {
    icon: <BarChart3 className="h-5 w-5 text-green-500" />,
    color: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800',
  },
  externalPortal: {
    icon: <Building2 className="h-5 w-5 text-indigo-500" />,
    color: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800',
  },
  commissions: {
    icon: <Percent className="h-5 w-5 text-rose-500" />,
    color: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800',
  },
  analytics: {
    icon: <LineChart className="h-5 w-5 text-sky-500" />,
    color: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800',
  },
  importing: {
    icon: <Upload className="h-5 w-5 text-lime-600" />,
    color: 'bg-lime-50 dark:bg-lime-950/30 border-lime-200 dark:border-lime-800',
  },
  scenarios: {
    icon: <SlidersHorizontal className="h-5 w-5 text-fuchsia-500" />,
    color: 'bg-fuchsia-50 dark:bg-fuchsia-950/30 border-fuchsia-200 dark:border-fuchsia-800',
  },
  bugReports: {
    icon: <Bug className="h-5 w-5 text-red-500" />,
    color: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
  },
  notifications: {
    icon: <Bell className="h-5 w-5 text-orange-400" />,
    color: 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800',
  },
  developerApi: {
    icon: <Code2 className="h-5 w-5 text-slate-700 dark:text-slate-300" />,
    color: 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700',
  },
  adminOverview: {
    icon: <ShieldCheck className="h-5 w-5 text-teal-600" />,
    color: 'bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-800',
  },
};

const renderBold = (text) =>
  String(text)
    .split('**')
    .map((part, i) =>
      i % 2 === 1 ? (
        <strong key={i} className="text-foreground font-medium">
          {part}
        </strong>
      ) : (
        <span key={i}>{part}</span>
      )
    );

export default function UserGuidePage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('guide');

  const categoriesRaw = t('guide.categories', { returnObjects: true });
  const faqRaw = t('guide.faq', { returnObjects: true });
  const categories = Array.isArray(categoriesRaw) ? categoriesRaw : [];
  const faq = Array.isArray(faqRaw) ? faqRaw : [];

  const q = searchQuery.toLowerCase();

  const filteredGuide = categories
    .map((cat) => ({
      ...cat,
      sections: (cat.sections || []).filter(
        (s) =>
          s.title.toLowerCase().includes(q) || s.content.toLowerCase().includes(q)
      ),
    }))
    .filter((cat) => cat.sections.length > 0);

  const filteredFaq = faq
    .map((cat) => ({
      ...cat,
      questions: (cat.questions || []).filter(
        (item) =>
          item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
      ),
    }))
    .filter((cat) => cat.questions.length > 0);

  return (
    <div className="min-h-screen bg-background" data-testid="user-guide-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8" data-testid="guide-header">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/40">
              <HelpCircle className="h-6 w-6 text-teal-600 dark:text-teal-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {t('guide.title')}
            </h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">{t('guide.subtitle')}</p>
        </div>

        {/* Search */}
        <div className="relative mb-6" data-testid="guide-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t('guide.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
            data-testid="guide-search-input"
          />
        </div>

        {/* Tab Switcher */}
        <div
          className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800/60 rounded-lg mb-8 w-fit"
          data-testid="guide-tab-switcher"
        >
          <button
            onClick={() => setActiveTab('guide')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'guide'
                ? 'bg-white dark:bg-slate-700 shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="guide-tab-guide"
          >
            <BookOpen className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            {t('guide.tabGuide')}
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'faq'
                ? 'bg-white dark:bg-slate-700 shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="guide-tab-faq"
          >
            <HelpCircle className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            {t('guide.tabFaq')}
          </button>
        </div>

        {/* GUIDE TAB */}
        {activeTab === 'guide' && (
          <div className="space-y-6" data-testid="guide-content">
            {filteredGuide.length === 0 && (
              <div
                className="text-center py-12 text-muted-foreground"
                data-testid="guide-no-results"
              >
                <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">
                  {t('guide.noResults')} "{searchQuery}"
                </p>
              </div>
            )}
            {filteredGuide.map((category) => {
              const meta = CATEGORY_META[category.id] || {};
              return (
                <div
                  key={category.id}
                  className={`rounded-xl border p-5 ${meta.color || ''}`}
                  data-testid={`guide-category-${category.id}`}
                >
                  <div className="flex items-center gap-2.5 mb-4">
                    {meta.icon}
                    <h2 className="text-lg font-semibold">{category.title}</h2>
                    <Badge variant="secondary" className="ml-auto text-[10px]">
                      {category.sections.length}{' '}
                      {category.sections.length === 1
                        ? t('guide.topic')
                        : t('guide.topics')}
                    </Badge>
                  </div>
                  <Accordion type="multiple" className="space-y-1">
                    {category.sections.map((section, idx) => (
                      <AccordionItem
                        key={idx}
                        value={`${category.id}-${idx}`}
                        className="border-0 bg-white/60 dark:bg-slate-900/40 rounded-lg px-4"
                      >
                        <AccordionTrigger
                          className="text-sm font-medium hover:no-underline py-3"
                          data-testid={`guide-section-${category.id}-${idx}`}
                        >
                          <span className="flex items-center gap-2">
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                            {section.title}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line pb-4 pl-6">
                          {renderBold(section.content)}
                          <GuideSectionVote sectionId={`${category.id}-${idx}`} />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ TAB */}
        {activeTab === 'faq' && (
          <div className="space-y-6" data-testid="faq-content">
            {filteredFaq.length === 0 && (
              <div
                className="text-center py-12 text-muted-foreground"
                data-testid="faq-no-results"
              >
                <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">
                  {t('guide.noResults')} "{searchQuery}"
                </p>
              </div>
            )}
            {filteredFaq.map((category, catIdx) => (
              <div key={catIdx} data-testid={`faq-category-${catIdx}`}>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-teal-500" />
                  {category.category}
                </h3>
                <Accordion type="multiple" className="space-y-1">
                  {category.questions.map((item, qIdx) => (
                    <AccordionItem
                      key={qIdx}
                      value={`faq-${catIdx}-${qIdx}`}
                      className="border rounded-lg px-4 bg-white dark:bg-slate-900/40"
                    >
                      <AccordionTrigger
                        className="text-sm font-medium hover:no-underline py-3 text-left"
                        data-testid={`faq-q-${catIdx}-${qIdx}`}
                      >
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
